/**
 * AES-GCM encryption helper for the DutiLyne Desktop Terminal's local cache
 * (products, prices, stock, recent customers, queued offline sales).
 *
 * Keeps the IndexedDB file itself unreadable if someone copies the device's
 * profile/disk — "a local secure cache... lives encrypted on the terminal
 * itself", exactly as decided for the offline-first desktop terminal.
 *
 * Key storage:
 *  - Inside the Electron desktop shell: the raw key is wrapped with the OS
 *    keychain via `safeStorage`, reached through the preload bridge
 *    (window.pharmxai.getSecureKey / setSecureKey).
 *  - In a plain browser tab (no Electron bridge, e.g. the web app itself):
 *    falls back to a random key kept in localStorage. Weaker than an OS
 *    keychain, but still keeps the IndexedDB bytes non-plaintext, and the
 *    cached data here is operational (products/prices/customers), not the
 *    clinical E2EE payloads handled separately under `care/crypto.js`.
 */

const KEY_STORAGE_KEY = 'pharmxai_offline_cache_key';
let cachedCryptoKey = null;

const bridge = () => (typeof window !== 'undefined' ? window.pharmxai : null);

function randomBytes(n) {
  const arr = new Uint8Array(n);
  crypto.getRandomValues(arr);
  return arr;
}

function bytesToBase64(bytes) {
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function getRawKeyBytes() {
  const b = bridge();
  if (b?.getSecureKey && b?.setSecureKey) {
    const existing = await b.getSecureKey();
    if (existing) return base64ToBytes(existing);
    const fresh = randomBytes(32);
    await b.setSecureKey(bytesToBase64(fresh));
    return fresh;
  }
  const existing = localStorage.getItem(KEY_STORAGE_KEY);
  if (existing) return base64ToBytes(existing);
  const fresh = randomBytes(32);
  localStorage.setItem(KEY_STORAGE_KEY, bytesToBase64(fresh));
  return fresh;
}

async function getCryptoKey() {
  if (cachedCryptoKey) return cachedCryptoKey;
  const raw = await getRawKeyBytes();
  cachedCryptoKey = await crypto.subtle.importKey('raw', raw, 'AES-GCM', false, ['encrypt', 'decrypt']);
  return cachedCryptoKey;
}

/** Encrypt any JSON-serializable value -> opaque base64 string (iv + ciphertext). */
export async function encryptValue(value) {
  const key = await getCryptoKey();
  const iv = randomBytes(12);
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  const combined = new Uint8Array(iv.length + ciphertext.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(ciphertext), iv.length);
  return bytesToBase64(combined);
}

/** Decrypt an opaque base64 string back into the original value. Returns null on failure. */
export async function decryptValue(encoded) {
  if (!encoded) return null;
  try {
    const key = await getCryptoKey();
    const combined = base64ToBytes(encoded);
    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);
    return JSON.parse(new TextDecoder().decode(plaintext));
  } catch (e) {
    console.error('Offline cache decrypt failed:', e);
    return null;
  }
}

export default { encryptValue, decryptValue };
